import type { ConnectorCatalogEntry, ConnectorConnection } from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Pill,
  Skeleton,
  cn,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  connectApiKey,
  connectionKeys,
  connectorCatalogQuery,
  disconnectConnector,
  importHistoryQuery,
  startOAuth,
  syncConnector,
} from '../../shared/api/connection-queries.js';
import { sessionQuery, setActiveOrganization } from '../../shared/api/queries.js';
import { connectionLine, invoicesIn, sinceLabel, syncSentence } from './connection-text.js';
import { ConnectorMark } from './connector-mark.js';

/**
 * Connections: the software the business already runs on.
 *
 * Whatever the source (a CRM's API, or a spreadsheet exported from one), it
 * lands the same way: customers and jobs matched by the system's own ids,
 * invoices as draft income at the tax that was actually charged, and nothing
 * counted twice. A business with a CRM nobody has built a connector for is
 * never stuck: the spreadsheet tile is the way in for every one of them.
 */

export interface ConnectionsSearch {
  connected?: string | undefined;
  error?: string | undefined;
  organization?: string | undefined;
}

export function ConnectionsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const membership = session.organizations.find(
    (entry) => entry.organization.id === organizationId,
  );
  const canManage = membership?.permissions.includes('organization:manage') ?? false;
  const canImport = membership?.permissions.includes('transaction:update') ?? false;
  const search: ConnectionsSearch = useSearch({ strict: false });
  const navigate = useNavigate();
  const catalog = useQuery(connectorCatalogQuery(organizationId));
  const history = useQuery(importHistoryQuery(organizationId));

  // A sign-in started during setup returns for the business being set up,
  // which may not be the one this browser has open.
  useEffect(() => {
    const target = search.organization;
    if (target !== undefined && target !== organizationId) {
      setActiveOrganization(target);
      const params = new URLSearchParams(window.location.search);
      params.delete('organization');
      window.location.assign(`/connections?${params.toString()}`);
    }
  }, [search.organization, organizationId]);

  const entries = catalog.data?.connectors ?? [];
  const connected = entries.filter(
    (entry) => entry.connection !== null && entry.connection.status !== 'disconnected',
  );
  const justConnected = entries.find(
    (entry) => entry.provider === search.connected && entry.connection !== null,
  );

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Business"
        title="Connections"
        description="Bring customers, jobs and invoices in from the software the business already runs on. Every source lands the same way: invoices become draft income at the tax that was actually charged, and nothing is ever counted twice."
      />

      {search.error === undefined ? null : (
        <Alert tone="danger" title="The connection did not finish">
          {search.error}
        </Alert>
      )}

      {catalog.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-44" />
          <Skeleton className="h-44" />
          <Skeleton className="h-44" />
        </div>
      ) : catalog.isError ? (
        <Alert tone="danger" title="Connections could not be loaded">
          {catalog.error.message}
        </Alert>
      ) : (
        <>
          {connected.length === 0 ? null : (
            <section className="flex flex-col gap-3">
              <SectionTitle
                title="Connected"
                note="Each one syncs by itself every night. Sync now to bring in today's work."
              />
              {connected.map((entry) => (
                <ConnectedCard
                  key={entry.provider}
                  organizationId={organizationId}
                  entry={entry}
                  connection={entry.connection as ConnectorConnection}
                  canManage={canManage}
                  syncOnArrival={justConnected?.provider === entry.provider}
                  onArrivalHandled={() =>
                    void navigate({ to: '/connections', search: {}, replace: true })
                  }
                />
              ))}
            </section>
          )}

          <section className="flex flex-col gap-3">
            <SectionTitle
              title={connected.length === 0 ? 'Connect a system' : 'Add another'}
              note="A business can connect more than one: an invoice that arrives from two places is linked, not doubled."
            />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {entries
                .filter((entry) => !connected.includes(entry))
                .map((entry) => (
                  <CatalogTile
                    key={entry.provider}
                    organizationId={organizationId}
                    entry={entry}
                    canManage={canManage}
                  />
                ))}
              <SpreadsheetTile canImport={canImport} />
            </div>
          </section>
        </>
      )}

      <HowItWorks />

      {history.data === undefined || history.data.runs.length === 0 ? null : (
        <Card>
          <CardHeader
            title="Spreadsheets brought in"
            description="Each import is on the record, with what it made and the rows it could not use."
          />
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-content-subtle shadow-[inset_0_-1px_0_var(--color-border)]">
                    <th className="px-5 py-3 font-semibold">File</th>
                    <th className="px-5 py-3 font-semibold">When</th>
                    <th className="px-5 py-3 text-right font-semibold">Added</th>
                    <th className="px-5 py-3 text-right font-semibold">Matched</th>
                    <th className="px-5 py-3 text-right font-semibold">Not used</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.runs.map((run) => (
                    <tr key={run.id} className="border-t border-border">
                      <td className="px-5 py-3">
                        <p className="font-medium text-content">{run.fileName ?? 'Spreadsheet'}</p>
                        {run.profileName === null ? null : (
                          <p className="text-xs text-content-muted">{run.profileName}</p>
                        )}
                      </td>
                      <td className="px-5 py-3 text-content-muted">{sinceLabel(run.createdAt)}</td>
                      <td className="tabular px-5 py-3 text-right">{run.createdCount}</td>
                      <td className="tabular px-5 py-3 text-right">{run.linkedCount}</td>
                      <td className="tabular px-5 py-3 text-right text-content-muted">
                        {run.skippedCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function SectionTitle({ title, note }: { title: string; note: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 className="text-lg font-semibold tracking-tight text-content">{title}</h2>
      <p className="text-sm text-content-muted">{note}</p>
    </div>
  );
}

function ConnectedCard({
  organizationId,
  entry,
  connection,
  canManage,
  syncOnArrival,
  onArrivalHandled,
}: {
  organizationId: string;
  entry: ConnectorCatalogEntry;
  connection: ConnectorConnection;
  canManage: boolean;
  syncOnArrival: boolean;
  onArrivalHandled: () => void;
}) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const arrived = useRef(false);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: connectionKeys.all(organizationId) });

  const sync = useMutation({
    mutationFn: () => syncConnector(organizationId, entry.provider),
    onSuccess: (summary) => {
      setError(null);
      setResult(syncSentence(entry.name, summary));
      refresh();
      void queryClient.invalidateQueries();
    },
    onError: (caught) => {
      setResult(null);
      setError(caught instanceof Error ? caught.message : 'The sync did not run.');
      refresh();
    },
  });

  // Back from signing in: the first sync starts by itself, so the person sees
  // their own customers and invoices arrive rather than an empty card.
  useEffect(() => {
    if (!syncOnArrival || arrived.current) return;
    arrived.current = true;
    sync.mutate();
    onArrivalHandled();
  }, [syncOnArrival, sync, onArrivalHandled]);

  const disconnect = useMutation({
    mutationFn: () => disconnectConnector(organizationId, entry.provider),
    onSuccess: () => {
      setConfirming(false);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The connection was not removed.'),
  });

  const failing = connection.status === 'failing';
  const summary = connection.lastSummary;

  return (
    <Card className={cn(failing && 'ring-1 ring-warning/40')}>
      <CardBody className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-4">
          <ConnectorMark kind={entry.provider} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-content">{entry.name}</h3>
              {failing ? (
                <Pill tone="warning">Needs attention</Pill>
              ) : sync.isPending ? (
                <Pill tone="info">Syncing</Pill>
              ) : (
                <Pill tone="success">Connected</Pill>
              )}
            </div>
            <p className="mt-0.5 truncate text-sm text-content-muted">
              {connectionLine(connection)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => sync.mutate()}
              isLoading={sync.isPending}
            >
              Sync now
            </Button>
            {!canManage ? null : confirming ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => disconnect.mutate()}
                  isLoading={disconnect.isPending}
                >
                  Disconnect
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
                Disconnect
              </Button>
            )}
          </div>
        </div>

        {confirming ? (
          <p className="text-sm text-content-muted">
            Disconnecting destroys the {entry.auth === 'api_key' ? 'key' : 'sign-in'} and stops the
            nightly sync. Everything already brought in stays in the books.
          </p>
        ) : null}

        {failing && connection.lastError !== null ? (
          <Alert tone="warning" title="The last sync failed">
            {connection.lastError}
          </Alert>
        ) : null}
        {result === null ? null : <Alert tone="success">{result}</Alert>}
        {error === null ? null : <Alert tone="danger">{error}</Alert>}

        {summary === null ? null : (
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[var(--radius-control)] border border-border bg-border sm:grid-cols-4">
            <Figure label="Customers" value={summary.customers.created} note="new last sync" />
            <Figure label="Jobs" value={summary.jobs.created} note="new last sync" />
            <Figure label="Invoices" value={invoicesIn(summary)} note="into the books" />
            <Figure
              label="Tax to check"
              value={summary.invoices.flagged}
              note={summary.invoices.flagged === 0 ? 'all plain' : 'waiting in Review'}
              warn={summary.invoices.flagged > 0}
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Figure({
  label,
  value,
  note,
  warn = false,
}: {
  label: string;
  value: number;
  note: string;
  warn?: boolean;
}) {
  return (
    <div className="bg-surface px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-content-subtle">{label}</p>
      <p className={cn('tabular mt-1 text-xl font-bold', warn ? 'text-warning' : 'text-content')}>
        {value.toLocaleString('en-CA')}
      </p>
      <p className="text-xs text-content-muted">{note}</p>
    </div>
  );
}

export function CatalogTile({
  organizationId,
  entry,
  canManage,
}: {
  organizationId: string;
  entry: ConnectorCatalogEntry;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: () => connectApiKey(organizationId, entry.provider, apiKey),
    onSuccess: () => {
      setApiKey('');
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: connectionKeys.all(organizationId) });
      // Straight into the first sync, as a sign-in does.
      void syncConnector(organizationId, entry.provider).then(
        () => void queryClient.invalidateQueries(),
        () => void queryClient.invalidateQueries({ queryKey: connectionKeys.all(organizationId) }),
      );
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'That key was not saved.'),
  });

  const signIn = useMutation({
    mutationFn: () => startOAuth(organizationId, entry.provider),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The sign-in could not start.'),
  });

  const soon = entry.availability !== 'available';

  return (
    <Card className={cn('flex h-full flex-col', soon && 'bg-surface-sunken/40')}>
      <CardBody className="flex flex-1 flex-col gap-4">
        <div className="flex items-center gap-3">
          <ConnectorMark kind={entry.provider} className={cn(soon && 'opacity-60 saturate-50')} />
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-content">{entry.name}</h3>
            <p className="text-xs text-content-subtle">
              {entry.auth === 'api_key' ? 'Connects with an API key' : 'Connects by signing in'}
            </p>
          </div>
          {entry.availability === 'coming_soon' ? (
            <Pill>Coming soon</Pill>
          ) : entry.availability === 'awaiting_approval' ? (
            <Pill tone="info">Almost ready</Pill>
          ) : null}
        </div>
        <p className="flex-1 text-sm leading-relaxed text-content-muted">{entry.tagline}</p>

        {error === null ? null : <Alert tone="danger">{error}</Alert>}

        {soon ? (
          <p className="text-xs leading-relaxed text-content-subtle">
            {entry.availability === 'awaiting_approval'
              ? `Built, and waiting on ${entry.name}'s approval of the connection. Until then, export invoices from ${entry.name} and import the spreadsheet.`
              : `Until then, export invoices from ${entry.name} and import the spreadsheet: they land exactly the same way.`}
          </p>
        ) : !canManage ? (
          <p className="text-xs text-content-subtle">An owner or admin connects this.</p>
        ) : entry.auth === 'oauth' ? (
          <Button
            onClick={() => signIn.mutate()}
            isLoading={signIn.isPending}
            className="self-start"
          >
            Sign in with {entry.name}
          </Button>
        ) : open ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              connect.mutate();
            }}
          >
            <Field label="API key" {...(entry.keyHelp === null ? {} : { hint: entry.keyHelp })}>
              {({ inputProps }) => (
                <Input
                  {...inputProps}
                  type="password"
                  autoComplete="off"
                  autoFocus
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                />
              )}
            </Field>
            <p className="text-xs text-content-subtle">
              It goes straight to an encrypted vault only the server can read. Nobody sees it again,
              here or anywhere.
            </p>
            <div className="flex gap-2">
              <Button
                type="submit"
                isLoading={connect.isPending}
                disabled={apiKey.trim().length < 10}
              >
                Connect
              </Button>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button variant="secondary" onClick={() => setOpen(true)} className="self-start">
            Connect {entry.name}
          </Button>
        )}
      </CardBody>
    </Card>
  );
}

function SpreadsheetTile({ canImport }: { canImport: boolean }) {
  const navigate = useNavigate();
  return (
    <Card className="flex h-full flex-col border-dashed bg-gradient-to-br from-amber-50/60 to-transparent dark:from-amber-500/[0.06]">
      <CardBody className="flex flex-1 flex-col gap-4">
        <div className="flex items-center gap-3">
          <ConnectorMark kind="spreadsheet" />
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-content">Any other CRM</h3>
            <p className="text-xs text-content-subtle">CSV or Excel export</p>
          </div>
          <Pill tone="success">Works today</Pill>
        </div>
        <p className="flex-1 text-sm leading-relaxed text-content-muted">
          Workiz, ServiceM8, Kickserv, Tradify, FieldEdge, or a spreadsheet you keep yourself.
          Export the invoices, match the columns once, and next month is a single drop.
        </p>
        {canImport ? (
          <Button
            onClick={() => void navigate({ to: '/connections/import' })}
            className="self-start"
          >
            Import a spreadsheet
          </Button>
        ) : (
          <p className="text-xs text-content-subtle">An owner, admin or bookkeeper imports.</p>
        )}
      </CardBody>
    </Card>
  );
}

function HowItWorks() {
  const steps = [
    {
      title: 'Matched, not copied',
      body: 'Customers and jobs are matched by the system’s own ids, so a nightly sync updates what changed and never makes a second Marie Leblanc.',
    },
    {
      title: 'Income at the real tax',
      body: 'Each invoice becomes a draft at the tax the CRM charged. A rate that is not a Canadian one is never guessed: it waits in Review.',
    },
    {
      title: 'Never counted twice',
      body: 'An invoice already in the books under the same number and amount is linked to it. A void in the CRM reverses the draft here.',
    },
  ];
  return (
    <div className="grid gap-px overflow-hidden rounded-[var(--radius-surface)] border border-border bg-border md:grid-cols-3">
      {steps.map((step, index) => (
        <div key={step.title} className="bg-surface p-5">
          <p className="tabular text-xs font-semibold text-brand-600">0{index + 1}</p>
          <h3 className="mt-1 font-semibold text-content">{step.title}</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-content-muted">{step.body}</p>
        </div>
      ))}
    </div>
  );
}
